import { mutate } from "swr";
import { Project } from "~/services/projects";
import { apiMutate } from "~/utils/apiClient";

// Quick action definitions are admin config for the platform's quick-action
// cards (agent/dataset/home surfaces), served by the Projects service. They
// sit alongside (not under) the `/projects` resource, so this doesn't reuse
// PROJECTS_API_PREFIX.
const { VITE_PROJECTS_API } = import.meta.env;
export const QUICK_ACTIONS_API_PREFIX = `${VITE_PROJECTS_API}/quick-actions`;

// Where a quick action card can be surfaced. `placement_id` is required for
// AGENT/DATASET (the resource the card is about) and omitted for HOME.
export const QuickActionPlacement = {
  HOME: "home",
  AGENT: "agent",
  DATASET: "dataset",
} as const;
export type QuickActionPlacement = (typeof QuickActionPlacement)[keyof typeof QuickActionPlacement];

// -----------------------------------------------------------------------
// Admin CRUD (manage:quick-actions) — list/get/create/update/delete the
// definitions themselves.
// -----------------------------------------------------------------------

export const quickActionListPath = (params?: { limit?: number; offset?: number }) => {
  const query = new URLSearchParams();
  if (params?.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params?.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  const qs = query.toString();
  return qs ? `${QUICK_ACTIONS_API_PREFIX}?${qs}` : QUICK_ACTIONS_API_PREFIX;
};

export const quickActionPath = (id: string) => `${QUICK_ACTIONS_API_PREFIX}/${id}`;

export interface QuickActionAgentRef {
  agent_id: string;
}

export interface QuickActionDatasetRef {
  dataset_id: string;
}

// The full admin definition, as returned by create/list/get/update.
export interface QuickAction {
  id: string;
  placement: QuickActionPlacement;
  placement_id: string | null;
  title: string;
  subtitle: string | null;
  icon: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  project_name: string;
  project_description: string | null;
  seed_prompt: string | null;
  agents: QuickActionAgentRef[];
  datasets: QuickActionDatasetRef[];
}

export interface QuickActionListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface QuickActionList {
  items: QuickAction[];
  metadata: QuickActionListMetadata;
}

export interface CreateQuickActionInput {
  placement: QuickActionPlacement;
  placement_id?: string | null;
  title: string;
  subtitle?: string | null;
  icon?: string | null;
  enabled?: boolean;
  project_name: string;
  project_description?: string | null;
  seed_prompt?: string | null;
  agent_ids?: string[];
  dataset_ids?: string[];
}

// Placement is immutable after creation — recreate the definition to move it
// to a different surface. Every field is optional; `agent_ids`/`dataset_ids`
// set-replace the attached collections when supplied.
export interface UpdateQuickActionInput {
  title?: string;
  subtitle?: string | null;
  icon?: string | null;
  enabled?: boolean;
  project_name?: string;
  project_description?: string | null;
  seed_prompt?: string | null;
  agent_ids?: string[];
  dataset_ids?: string[];
}

export async function createQuickAction(input: CreateQuickActionInput): Promise<QuickAction> {
  return apiMutate<QuickAction>(QUICK_ACTIONS_API_PREFIX, "POST", input);
}

export async function updateQuickAction(
  id: string,
  input: UpdateQuickActionInput
): Promise<QuickAction> {
  return apiMutate<QuickAction>(quickActionPath(id), "PATCH", input);
}

export async function deleteQuickAction(id: string): Promise<void> {
  await apiMutate<null>(quickActionPath(id), "DELETE", undefined, { expects204: true });
}

// -----------------------------------------------------------------------
// End-user surfaces (use:agents) — the available-actions list a placement
// renders as cards, and invoking one.
// -----------------------------------------------------------------------

export const quickActionsAvailablePath = (
  placement: QuickActionPlacement,
  placementId?: string
) => {
  const query = new URLSearchParams({ placement });
  if (placementId) {
    query.set("placement_id", placementId);
  }
  return `${QUICK_ACTIONS_API_PREFIX}/available?${query.toString()}`;
};

export const quickActionInvokePath = (quickActionId: string) =>
  `${QUICK_ACTIONS_API_PREFIX}/${quickActionId}/invoke`;

// Revalidate every cached available-actions key (all placements/ids). Used
// after a 404 on invoke, since that means the definition was disabled or
// deleted after the list was fetched — the visible card is stale.
export const mutateQuickActionsAvailable = () =>
  mutate(
    (key) => typeof key === "string" && key.startsWith(`${QUICK_ACTIONS_API_PREFIX}/available?`)
  );

// The lightweight card shape returned by `/available` — distinct from the
// full admin `QuickAction` definition above.
export interface QuickActionCard {
  id: string;
  title: string;
  subtitle: string;
  // A lucide-react dynamic icon name (e.g. "chart-column"), validated before render.
  icon: string;
}

// The session the invoke opens for the materialized project. The frontend only
// consumes its id, to route into the conversation. Null only when invoked
// against a pre-session backend.
export interface QuickActionSession {
  id: string;
}

export interface InvokeQuickActionResponse {
  project: Project;
  session: QuickActionSession | null;
  // The definition's seed prompt, returned for the client to pre-fill the new
  // session's composer. Not persisted server-side; null when the definition has
  // no seed prompt.
  seed_prompt: string | null;
  reused: boolean;
}

export async function invokeQuickAction(quickActionId: string): Promise<InvokeQuickActionResponse> {
  return apiMutate<InvokeQuickActionResponse>(quickActionInvokePath(quickActionId), "POST");
}
